import {
  createContext,
  useState,
  FunctionComponent,
  useContext,
  useCallback,
} from 'react'

import type { INPS, IPathSelected } from '../../types'
import type { NPSContextType } from './types'

const NPSContext = createContext<NPSContextType>(null)

export const NPSProvider: FunctionComponent = ({ children }) => {
  const [NPSAnswers, setNPSAnswers] = useState<INPS>({})
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [selectedPath, setSelectedPath] = useState<IPathSelected>(null)

  const updateNPSAnswers = useCallback((updatedNPSAnswers: INPS) => {
    setNPSAnswers(updatedNPSAnswers)
  }, [])

  const updateNPSAnswersPartially = useCallback(
    (updatedNPSAnswers: Partial<INPS>) => {
      setNPSAnswers(prevState => {
        return {
          ...prevState,
          ...updatedNPSAnswers,
        }
      })
    },
    [],
  )

  const nextStep = useCallback(() => {
    setCurrentStep(currentStep + 1)
  }, [currentStep])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }, [currentStep])

  const choosePath = useCallback(
    (path: IPathSelected) => {
      setSelectedPath(path)
      nextStep()
    },
    [nextStep],
  )

  return (
    <NPSContext.Provider
      value={{
        NPSAnswers,
        updateNPSAnswers,
        updateNPSAnswersPartially,
        currentStep,
        selectedPath,
        choosePath,
        nextStep,
        prevStep,
      }}
    >
      {children}
    </NPSContext.Provider>
  )
}

export const useNPSContext = (): NPSContextType => {
  const context = useContext(NPSContext)

  if (!context) {
    throw new Error('useNPSContext must be used within a NPSProvider')
  }

  return context
}
